import { observable, action } from "mobx";
import { validarCamposObrigatorios, isNuloOuVazio } from "../../../../utils/Validate";
import * as util from "../../../../utils/MascaraUtil";
import { Orcamento } from "../../../../services";
import { error, success } from "../../../../utils/MensagemUtil";

class CronogramaStore {
  @observable
  orcamento = {
    etapas: []
  };

  @observable
  estimativa = "";

  @observable
  fetching = false;

  @action.bound
  async obterOrcamento(id) {
    try {
      this.fetching = true;

      const result = await Orcamento.buscarCronograma(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      // console.log(result);
      this.orcamento = {
        ...result,
        etapas: result.etapas.map(etapa => ({
          ...etapa,
          itens: etapa.itens.map(item => ({
            ...item,
            estimativas: item.estimativas
              ? item.estimativas.map(e => ({
                  ...e,
                  id_etapa: etapa.id
                }))
              : []
          }))
        }))
      };

      this.calcularTotais();
    } finally {
      this.fetching = false;
    }
  }

  calcularTotais = () => {
    this.orcamento.etapas.forEach(etapa => {
      let valor_total_geral = 0;

      etapa.itens.forEach(item => {
        let total_percentual_item = 0;
        let total_dias_item = 0;
        let total_valor_item = 0;

        if (!isNuloOuVazio(item.estimativas)) {
          item.estimativas.forEach(estimativa => {
            estimativa.valor = (item.valorTotal * estimativa.percentual) / 100;

            total_percentual_item += estimativa.percentual;
            total_dias_item += estimativa.dias ? parseInt(estimativa.dias, 0) : 0;
            total_valor_item += estimativa.valor;
          });

          if (total_percentual_item > 100) {
            error("O total percentual não pode ser maior que 100%!");
          }
        }

        item.total_percentual = total_percentual_item;
        item.total_dias = total_dias_item;
        item.total_valor = total_valor_item;

        valor_total_geral += item.valorTotal; // TODO: obs nao bate
      });

      etapa.valor_total_geral = valor_total_geral;
    });

    this.calcularTotaisGerais();
  };

  calcularTotaisGerais = () => {
    this.orcamento.etapas = this.orcamento.etapas.map(etapa => {
      let totais = [];

      etapa.itens.forEach(item => {
        if (!isNuloOuVazio(item.estimativas)) {
          item.estimativas.forEach(estimativa => {
            const total = totais.find(t => t.nome === estimativa.nome);

            if (!total) {
              totais.push({
                nome: estimativa.nome,
                valor_mensal: estimativa.valor,
                valor_acumulado: estimativa.valor,
                valor_percentual: 0
              });
            } else {
              total.valor_mensal += estimativa.valor;
              total.valor_acumulado += estimativa.valor;
              total.valor_percentual = etapa.valor_total_geral;
            }
          });
        }
      });

      let anterior;
      totais = totais.map(total => {
        const valor_acumulado = total.valor_mensal + (anterior ? anterior.valor_acumulado : 0);
        const valor_percentual = (valor_acumulado / total.valor_percentual) * 100;

        anterior = {
          ...total,
          valor_acumulado,
          valor_percentual
        };

        return anterior;
      });

      return {
        ...etapa,
        totais
      };
    });
  };

  @action.bound
  handleChange = ({ target }) => {
    const { value } = target;
    this.estimativa = value;
  };

  @action.bound
  handleBlur = ({ target }) => {
    const { name, value } = target;
    const [campo, nome, item_id] = name.split("-");

    if (value || parseInt(value, 0) === 0) {
      this.orcamento.etapas.forEach(etapa => {
        etapa.itens.forEach(item => {
          item.estimativas.forEach(estimativa => {
            if (estimativa.nome === nome && estimativa.id_item === parseInt(item_id, 0)) {
              const valor = util.remover(value);
              estimativa[campo] = valor;
            }
          });
        });
      });
    }

    this.calcularTotais();
  };

  @action.bound
  definirNome = ({ target }) => {
    const { name, value } = target;
    const [nome, etapa_id] = name.split("-");

    if (value) {
      const erros = this.validarInclusaoEstimativa(value);
      if (erros) {
        error(erros);
        return;
      }

      this.orcamento.etapas.forEach(etapa => {
        if (etapa.id === parseInt(etapa_id, 0)) {
          etapa.itens.forEach(item => {
            item.estimativas.forEach(estimativa => {
              if (estimativa.nome === nome) {
                estimativa.nome = value;
              }
            });
          });

          const total = etapa.totais.find(t => t.nome === nome);
          if (total) total.nome = value;
        }
      });
    }
  };

  @action.bound
  incluirEstimativa = () => {
    const erros = this.validarInclusaoEstimativa(this.estimativa);
    if (erros) {
      error(erros);
      return;
    }

    this.orcamento.etapas = this.orcamento.etapas.map(etapa => ({
      ...etapa,
      itens: etapa.itens.map(item => {
        const estimativas = item.estimativas || [];

        estimativas.push({
          nome: this.estimativa,
          dias: 0,
          valor: 0,
          percentual: 0,
          id_item: item.id,
          id_etapa: etapa.id
        });

        return {
          ...item,
          estimativas
        };
      })
    }));

    this.calcularTotaisGerais();
  };

  @action.bound
  excluirEstimativa = estimativa => {
    this.orcamento.etapas.forEach(etapa => {
      if (etapa.id === estimativa.id_etapa) {
        etapa.itens.forEach(item => {
          item.estimativas.forEach(e => {
            if (e.nome === estimativa.nome) {
              item.estimativas.remove(e);
            }
          });
        });
      }
    });

    this.calcularTotais();
  };

  validarInclusaoEstimativa = nome => {
    if (nome == null || nome === "") {
      return "O campo 'Nome' precisa ser preenchido!";
    }

    let erro = null;
    this.orcamento.etapas.forEach(etapa => {
      etapa.itens.forEach(item => {
        const existente = item.estimativas ? item.estimativas.find(m => m.nome.trim() === nome.trim()) : null;
        if (existente) {
          erro = "Este nome já existe!";
        }
      });
    });

    return erro;
  };

  @action.bound
  async salvar() {
    try {
      this.fetching = true;

      const erros = this.validarCronogramaOrcamento();
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Orcamento.atualizarCronograma(this.toOrcamento());
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Orçamento atualizado com sucesso!");
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async concluir(history) {
    try {
      await this.salvar();

      this.fetching = true;

      const result = await Orcamento.concluir({ id: this.orcamento.id });
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Orçamento finalizado com sucesso!");

      history.push(`/cadastros/orcamentos/`);
    } finally {
      this.fetching = false;
    }
  }

  validarCronogramaOrcamento = () => {
    let valido = false;
    this.orcamento.etapas.forEach(etapa => {
      etapa.itens.forEach(item => {
        if (item.estimativas && item.estimativas.length > 0) {
          valido = true;
        }
      });
    });

    const erros = validarCamposObrigatorios();
    if (!valido) {
      erros.addMensagem("Este orçamento não pode ser alterado porquê nenhuma estimativa foi incluída!");
    }

    return erros.build();
  };

  toOrcamento() {
    const novo = [];
    this.orcamento.etapas.forEach(etapa => {
      etapa.itens.forEach(item => {
        novo.push({
          ...item,
          estimativas: item.estimativas.map(estimativa => ({
            ...estimativa,
            dias: parseInt(estimativa.dias, 0),
            percentual: util.remover(estimativa.percentual),
            valor: util.remover(estimativa.valor)
          }))
        });
      });
    });

    return novo;
  }
}

export default new CronogramaStore();



// WEBPACK FOOTER //
// ./src/views/cadastros/orcamentos/stores/CronogramaStore.js